// @flow
import camelCase from 'lodash-es/camelCase';
import upperFirst from 'lodash-es/upperFirst';
import BaseComponent from '../../components/base_component/base_component';

class ComponentLocator {
    componentFactories: Map<string, (number, string, any) => BaseComponent>;
    componentsById: Map<number, BaseComponent>;
    componentsByName: Map<string, Array<BaseComponent>>;
    functions: {[string]: Function}
    f: {[string]: Function}
    functionList: Map<string, Function[]>
    awaiters: Array<() => void>;
    _loaded: boolean;

    constructor() {
        this.awaiters = [];
        this.clear();
    }

    clear() {
        this.componentFactories = new Map();
        let componentsById = this.componentsById = new Map();
        let componentsByName = this.componentsByName = new Map();
        this.functions = {};
        window._functions = this.f = this.functions;
        this.functionList = new Map();
        window.getComponent = (id: Number | any) => {
            if (typeof(id) === 'object') {
                id = $(id).closest('.wrapper').attr('data-component-id');
                return componentsById.get(id) || componentsById.get(parseInt(id));
            } else if(isNaN(id)) {
                let components = (componentsByName.get(id) || [null]);
                if (components.length !== 1) {
                    throw `Expected 1 component and found ${components.length}`;
                }
                return components[0];
            }
            return componentsById.get(id);
        }
        this._loaded = false;
    }

    /**
     * Sets whether the components are all loaded. Should be set to true only once
     * @param isLoaded Whether the component is loaded.
     */
    set loaded(isLoaded: boolean) {
        if (isLoaded && this._loaded) return;

        this._loaded = isLoaded;
        if (isLoaded) {
            this.awaiters.forEach(awaiter => awaiter());
            this.awaiters = [];
            for(let componentId of (window.__componentAdapter.latestBatch || [])) {
                let component = this.componentsById[componentId];
                component && component.onReady();
            };
            if (!this.f.handleAjaxError) return;

            window.__componentAdapter.ajaxErrors.forEach(e => {
                this.f.handleAjaxError(...e)
            });
        }
    }

    get loaded(): boolean {
        return this._loaded;
    }

    async waitUntilLoaded() {
        if (this._loaded) return;

        return new Promise(resolve => this.awaiters.push(resolve));
    }

    registerComponentFactory(name: string, factory: (number, any) => BaseComponent) {
        this.componentFactories.set(upperFirst(camelCase(name)), factory);
    }

    createComponent(id: number, name: string, options: any, extra_params: any, jsc: ?string) {
        let component = this.getFactory(name, jsc)(id, options, extra_params);
        if (!(component instanceof BaseComponent)) return;
        
        this.componentsById.set(id, component);
        let existingComponents = this.componentsByName.get(name);
        if (existingComponents) {
            existingComponents.push(component);
        } else {
            this.componentsByName.set(name, [component]);
        }
        let functions = component.functions();
        Object.keys(functions).forEach(func => {
            this.addFunction(func, functions[func]);
        })
        return component;
    }

    async deleteComponent(id: number) {
        let component: any = this.componentsById.get(id);
        if (!component) return;

        this.componentsById.delete(id);
        let byName = this.componentsByName.get(component.component_name) || [];
        byName.splice(byName.indexOf(component), 1);

        let functions = component.functions();
        Object.keys(functions).forEach(func => {
            let functions = this.functionList.get(func) || [];
            functions.splice(functions.indexOf(component), 1);
        })
        if (component.finalize.then) {
            await component.finalize();
        } else {
            component.finalize();
        }
    }

    addFunction(name: string, f: Function) {
        if (!this.functionList.get(name)) {
            this.functionList.set(name, []);
        }
        (this.functionList.get(name) || []).push(f);
        this.functions[name] = async (...args: any[]) => {
            await this.waitUntilLoaded();
            return (this.functionList.get(name) || []).map(f => f(...args));
        }
    }

    getFactory(name: string, fallbackClass: ?string): (number, any, any) => BaseComponent {
        let titleizedName = upperFirst(camelCase(name));

        let factory = this.componentFactories.get(titleizedName);
        if (!factory) {
            fallbackClass = fallbackClass || 'base_component/base_component';
            let constructor = require(`../../components/${fallbackClass}.js`);

            factory = (id, options, extra_params) => new constructor.default(id, name, options, extra_params);
            this.componentFactories.set(titleizedName, factory);
        }
        return factory;
    }
 
    async getById(componentId: number): Promise<?any> {
        await this.waitUntilLoaded();
        
        return this.componentsById.get(componentId);
    }

    async getAll(componentName: string): Promise<Array<any>> {
        await this.waitUntilLoaded();

        return this.componentsByName.get(componentName) || [];
    }

    async getSingle(componentName: string): Promise<any> {
        await this.waitUntilLoaded();

        let allComponents: ?Array<BaseComponent> = await this.getAll(componentName);
        if (!allComponents || allComponents.length === 0) {
            throw 'Could not find a component with name ' + componentName;
        }

        if (allComponents.length > 1) {
            throw 'Found multiple components with name ' + componentName;
        }
        return allComponents[0];
    }
}

export default new ComponentLocator();
